import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BulkImport from "@/components/BulkImport";
import RichTextEditor from "@/components/RichTextEditor";
import LegalFontToggle, { loadLegalFont, legalFontClass } from "@/components/LegalFontToggle";
import QuickJudgementBuilder from "@/components/QuickJudgementBuilder";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";

export default function LegalAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", court: "", content: "", content_html: "", difficulty: "medium" });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewFont, setPreviewFont] = useState(() => loadLegalFont());
  const load = () => api.get("/legal-passages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ title: "", court: "", content: "", content_html: "", difficulty: "medium" });
    setEditingId(null);
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) { toast.error("Passage content is required"); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/admin/legal-passages/${editingId}`, form);
        toast.success("Passage updated");
      } else {
        await api.post("/admin/legal-passages", form);
        toast.success("Passage added");
      }
      resetForm();
      load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const edit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || "",
      court: p.court || "",
      content: p.content || "",
      content_html: p.content_html || p.content || "",
      difficulty: p.difficulty || "medium",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/legal-passages/${id}`);
    if (editingId === id) resetForm();
    load();
  };

  return (
    <div className="grid grid-cols-1 gap-6">
      <QuickJudgementBuilder onCompile={(data) => {
        setEditingId(null);
        setForm((f) => ({ ...f, ...data }));
        toast.success("Compiled — review below and click Add passage");
        // Scroll to the main form so admin can see the auto-filled content
        setTimeout(() => document.querySelector('[data-testid="admin-legal-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }} />

      <form onSubmit={add} className="border border-black/20 p-6 space-y-4" data-testid="admin-legal-form">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold flex items-center gap-2">
              {editingId ? <span className="text-brand-crimson">Editing passage · </span> : null}
              {editingId ? "update legal passage" : "Add legal passage · with formatting"}
            </div>
            <div className="text-[11px] font-mono text-muted-foreground mt-1">
              {editingId
                ? "Adjust any field below and click Update. Cancel to leave the item unchanged."
                : "Content Studio gives you a full legal-page canvas below. Paste the entire judgement — no truncation."}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {editingId && (
              <Button
                type="button"
                onClick={resetForm}
                variant="outline"
                className="rounded-none h-10 px-4 border-black/30 text-neutral-700 hover:bg-neutral-100 text-xs uppercase tracking-widest font-bold"
                data-testid="admin-legal-cancel-edit-btn"
              >
                Cancel
              </Button>
            )}
            <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
              <SelectTrigger className="rounded-none w-40" data-testid="admin-legal-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-lg font-mono" data-testid="admin-legal-title" />
          <Input required placeholder="Court name" value={form.court} onChange={(e) => setForm({ ...form, court: e.target.value })} className="rounded-lg font-mono" data-testid="admin-legal-court" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground mb-2">Judgement text (500–1000 words) — use bold / italic / center to mirror real court formatting</div>
          <div className="text-[9px] uppercase tracking-widest font-bold text-brand-gold mb-3">✍️  Editor · Legal-size page (8.5" × 14")</div>
          <RichTextEditor
            value={form.content_html}
            onChange={(html, text) => setForm((f) => ({ ...f, content_html: html, content: text }))}
            placeholder="Paste the judgement here and apply the same bold / italic / center-align it has on court letterhead…"
            testId="admin-legal-content-rte"
            size="legal"
          />

          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[9px] uppercase tracking-widest font-bold text-brand-gold">👁️  Student preview · same page</div>
            <LegalFontToggle value={previewFont} onChange={setPreviewFont} testId="admin-legal-preview-font" />
          </div>
          <div
            className={`mt-3 legal-page min-h-[1344px] practice-typing-area ${legalFontClass(previewFont)}`}
            data-testid="admin-legal-preview"
            data-font={previewFont}
            style={{ lineHeight: 1.55, whiteSpace: "pre-wrap" }}
          >
            <div className="legal-page__watermark">Student preview</div>
            {form.content_html
              ? <div dangerouslySetInnerHTML={{ __html: form.content_html }} />
              : <div className="text-muted-foreground italic text-base">Preview appears here as you type. Students will see the passage in whichever serif you pick above.</div>}
          </div>
          <div className="mt-2 text-[10px] font-mono text-muted-foreground text-right" data-testid="admin-legal-wordcount">
            {(form.content || "").trim().split(/\s+/).filter(Boolean).length} words
          </div>
        </div>
        <Button type="submit" disabled={busy} className={`rounded-none w-full h-11 ${editingId ? "bg-brand-crimson hover:bg-brand-crimson/90" : "bg-[#0A2540] hover:bg-[#002FA7]"} text-white text-xs uppercase tracking-[0.2em] font-bold`} data-testid="admin-legal-submit-btn">
          {busy ? (editingId ? "Updating…" : "Adding…") : (editingId ? "Update passage" : "Add passage")}
        </Button>
      </form>

      <div className="md:col-span-2">
        <BulkImport
          module="legal"
          requiredFields={["title", "court", "content"]}
          optionalFields={["difficulty"]}
          onImported={load}
        />
      </div>

      <div>
        <h3 className="text-lg font-black tracking-tight mb-3">Existing passages</h3>
        <div className="border border-black/15 divide-y divide-black/10" data-testid="admin-legal-list">
          {items.length === 0 && <div className="p-4 font-mono text-sm text-neutral-500">None yet.</div>}
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-bold">{p.title}</div>
                <div className="text-xs font-mono text-neutral-500">{p.court} · {p.word_count} words · {p.difficulty}</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => edit(p)} variant="outline" size="sm" className="rounded-none border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white" data-testid={`admin-legal-edit-${p.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button onClick={() => del(p.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-700 hover:bg-red-50" data-testid={`admin-legal-delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
